import React from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    ModalCloseButton, Button, HStack, Text, Badge, Box, Icon, Flex, Spinner, Center,
    useBreakpointValue,
} from "@chakra-ui/react";
import { FiDownload, FiExternalLink, FiFileText } from "react-icons/fi";
import { formatCurrency } from "../utils/formatHelpers";

const getFileId = (pdfUrl) => {
    const match = pdfUrl?.match(/\/file\/d\/([^/]+)/);
    return match ? match[1] : null;
};

const getDownloadUrl = (pdfUrl) => {
    const fileId = getFileId(pdfUrl);
    return fileId ? `https://drive.google.com/uc?export=download&id=${fileId}` : pdfUrl;
};

const getPreviewUrl = (pdfUrl) => {
    const fileId = getFileId(pdfUrl);
    return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : pdfUrl;
};

export const InvoicePdfModal = ({ invoice, isOpen, onClose }) => {
    const downloadLabel = useBreakpointValue({ base: "Download", md: "Download PDF" });
    const openLabel = useBreakpointValue({ base: "Open", md: "Open in new tab" });

    if (!invoice) return null;

    const isPaid = invoice.status === "paid" || invoice.status === "Paid";
    const subtotal = invoice.invoice_line_items?.reduce(
        (s, item) => s + item.unit_price * item.quantity, 0
    ) ?? 0;
    const gst = invoice.gst_amount ?? 0;
    const total = subtotal + gst;
    const description = invoice.invoice_line_items?.map((i) => i.description).join(", ")
        || invoice.invoice_type
        || "Invoice";

    const previewUrl = invoice.pdf_url ? getPreviewUrl(invoice.pdf_url) : null;
    const downloadUrl = invoice.pdf_url ? getDownloadUrl(invoice.pdf_url) : null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={{ base: "full", md: "4xl" }}
            scrollBehavior="inside"
            isCentered
        >
            <ModalOverlay />
            <ModalContent
                maxH={{ base: "100vh", md: "92vh" }}
                h={{ base: "100vh", md: "92vh" }}
                m={{ base: 0, md: "auto" }}
                borderRadius={{ base: 0, md: "xl" }}
                display="flex"
                flexDirection="column"
                overflow="hidden"
            >
                {/* Header */}
                <ModalHeader
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    py={{ base: 3, md: 4 }}
                    px={{ base: 4, md: 6 }}
                    flexShrink={0}
                >
                    <Flex
                        justify="space-between"
                        align={{ base: "flex-start", md: "center" }}
                        pr={8}
                        flexWrap="wrap"
                        gap={2}
                    >
                        <HStack spacing={3} align="flex-start">
                            <Icon as={FiFileText} color="brand.500" boxSize={5} mt={0.5} />
                            <Box>
                                <Text fontSize="md" fontWeight="bold" lineHeight="1.2" color="brand.700">
                                    {invoice.invoice_number || "Invoice"}
                                </Text>
                                <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={0.5} noOfLines={1}>
                                    {description}
                                </Text>
                            </Box>
                        </HStack>
                        <HStack spacing={2} flexShrink={0}>
                            <Text fontWeight="bold" fontSize="lg" color={isPaid ? "green.600" : "gray.800"}>
                                {formatCurrency(total)}
                            </Text>
                            <Badge
                                colorScheme={isPaid ? "green" : "orange"}
                                px={2}
                                py={0.5}
                                borderRadius="md"
                                textTransform="capitalize"
                            >
                                {invoice.status}
                            </Badge>
                        </HStack>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton top={3} />

                {/* PDF Body */}
                <ModalBody p={0} flex="1" overflow="hidden" bg="gray.100" display="flex" flexDirection="column">
                    {previewUrl ? (
                        <iframe
                            src={previewUrl}
                            title={`Invoice ${invoice.invoice_number}`}
                            width="100%"
                            height="100%"
                            style={{ border: "none", display: "block", flex: 1, minHeight: 0 }}
                            allow="autoplay"
                        />
                    ) : (
                        <Center h="100%" flexDirection="column" gap={4}>
                            <Spinner size="lg" color="brand.500" thickness="3px" />
                            <Text color="gray.500" fontSize="sm" textAlign="center" px={4}>
                                The invoice PDF is still being generated.
                                <br />
                                Please check back in a moment.
                            </Text>
                        </Center>
                    )}
                </ModalBody>

                {/* Footer */}
                <ModalFooter
                    borderTop="1px solid"
                    borderColor="gray.100"
                    py={3}
                    px={{ base: 4, md: 6 }}
                    flexShrink={0}
                    bg="white"
                >
                    <Flex
                        direction={{ base: "column", md: "row" }}
                        gap={2}
                        w="100%"
                        justify={{ base: "stretch", md: "flex-end" }}
                        align={{ base: "stretch", md: "center" }}
                        flexWrap="wrap"
                    >
                        <Button
                            as="a"
                            href={downloadUrl ?? "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            leftIcon={<Icon as={FiDownload} flexShrink={0} />}
                            colorScheme="teal"
                            size="sm"
                            w={{ base: "100%", md: "auto" }}
                            minW={0}
                            maxW={{ md: "280px" }}
                            flex={{ md: "0 1 auto" }}
                            isDisabled={!downloadUrl}
                            whiteSpace="normal"
                            h="auto"
                            py={2}
                            lineHeight="1.25"
                            textAlign="center"
                            title="Download PDF"
                        >
                            {downloadLabel}
                        </Button>
                        <Button
                            as="a"
                            href={invoice.pdf_url ?? "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            leftIcon={<Icon as={FiExternalLink} flexShrink={0} />}
                            variant="outline"
                            colorScheme="gray"
                            size="sm"
                            w={{ base: "100%", md: "auto" }}
                            minW={0}
                            maxW={{ md: "280px" }}
                            flex={{ md: "0 1 auto" }}
                            isDisabled={!invoice.pdf_url}
                            whiteSpace="normal"
                            h="auto"
                            py={2}
                            lineHeight="1.25"
                            textAlign="center"
                            title="Open in new tab"
                        >
                            {openLabel}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            w={{ base: "100%", md: "auto" }}
                            minW={{ md: "5rem" }}
                            flexShrink={{ md: 0 }}
                            whiteSpace="normal"
                            h="auto"
                            py={2}
                            lineHeight="1.25"
                        >
                            Close
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
